import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { RefreshCw } from 'lucide-react';
import { Button } from '../../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const RANGES = [{ d: 1, l: 'Today' }, { d: 7, l: '7d' }, { d: 30, l: '30d' }, { d: 90, l: '90d' }];
const usd = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const ct = (iso) => new Date(iso).toLocaleString('en-US', { timeZone: 'America/Chicago', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });

const Kpi = ({ label, value, sub, color, testid }) => (
    <div className="p-4 rounded-lg border border-white/10 bg-white/[0.02]" data-testid={testid}>
        <div className="text-[10px] uppercase tracking-wide text-[#A0AAB2]">{label}</div>
        <div className="font-mono font-black text-xl mt-1" style={{ color: color || '#fff' }}>{value}</div>
        {sub != null && <div className="text-[10px] text-[#A0AAB2] mt-0.5">{sub}</div>}
    </div>
);

const Rounds = ({ rows }) => (
    <div className="overflow-x-auto pt-2 border-t border-white/5">
        <div className="text-[10px] uppercase tracking-wide text-[#A0AAB2] mb-2">Latest rounds (server record)</div>
        <table className="w-full text-xs" data-testid="bb-rounds-table">
            <thead><tr className="text-[#A0AAB2] text-left">
                {['Time (CT)', 'Player', 'Type', 'Bet', 'Win', 'Bonus', 'Round'].map((h) => <th key={h} className="py-1 pr-3 font-normal">{h}</th>)}
            </tr></thead>
            <tbody className="font-mono">
                {rows.map((r) => (
                    <tr key={r.id} className="border-t border-white/5">
                        <td className="py-1 pr-3 text-white">{ct(r.created_at)}</td>
                        <td className="py-1 pr-3 text-white">{r.player}</td>
                        <td className="py-1 pr-3 text-[#A0AAB2]">{r.kind}</td>
                        <td className="py-1 pr-3 text-[#ffd24a]">{r.bet ? usd(r.bet) : '—'}</td>
                        <td className="py-1 pr-3 text-[#3aa0ff]">{usd(r.win)}</td>
                        <td className="py-1 pr-3 text-[#A0AAB2]">{r.bonus || ''}</td>
                        <td className="py-1 pr-3 text-[#6b7280]">{r.round_id.slice(0, 8)}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    </div>
);

export const BuffaloBucksReportsAdminTab = () => {
    const [days, setDays] = useState(1);
    const [rep, setRep] = useState(null);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async (d) => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/admin/buffalo-bucks/report?days=${d}`, { withCredentials: true });
            setRep(data);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to load Buffalo Bucks report');
        } finally { setLoading(false); }
    }, []);
    useEffect(() => { load(days); }, [days, load]);

    return (
        <div className="card-dark p-5 space-y-4" data-testid="bb-reports-admin">
            <div className="flex items-center justify-between flex-wrap gap-2">
                <h3 className="text-sm uppercase font-bold text-white tracking-wide">Buffalo Bucks — Reports</h3>
                <div className="flex items-center gap-1">
                    {RANGES.map((r) => (
                        <Button key={r.d} size="sm" onClick={() => setDays(r.d)} variant={days === r.d ? 'default' : 'ghost'}
                            className={`h-8 text-xs ${days === r.d ? 'bg-[#E3AE54] text-black hover:bg-[#E3AE54]/90' : 'text-[#A0AAB2]'}`}
                            data-testid={`bb-report-range-${r.d}`}>{r.l}</Button>
                    ))}
                    <Button size="sm" variant="ghost" onClick={() => load(days)} className="text-[#A0AAB2] h-8 text-xs" data-testid="bb-report-refresh">
                        <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
                    </Button>
                </div>
            </div>
            <p className="text-[11px] text-[#A0AAB2]">
                From the transaction ledger, business days 7 AM–7 AM Central (same as Money summary). Handle = paid bets, Wins = line wins + bonus prizes, GGR = handle − wins − shared jackpots. Not profit.
            </p>
            {!rep ? <div className="text-[#A0AAB2] py-12 text-center">Loading…</div> : (
                <>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <Kpi label="Handle (wagered)" value={usd(rep.handle)} sub={`${rep.spins.toLocaleString()} paid spins`} color="#ffd24a" testid="bb-kpi-handle" />
                        <Kpi label="Game wins" value={usd(rep.wins)} color="#3aa0ff" testid="bb-kpi-wins" />
                        <Kpi label="Shared jackpots paid" value={usd(rep.jackpot_payouts)} sub={`${rep.jackpot_hits} hits`} color="#b06bff" testid="bb-kpi-jackpots" />
                        <Kpi label="GGR (house)" value={usd(rep.ggr)} color={rep.ggr >= 0 ? '#3ad07a' : '#ff5a5a'} testid="bb-kpi-ggr" />
                        <Kpi label="Game RTP" value={`${rep.rtp_pct}%`} sub={`design ${rep.target_rtp_pct}% · excl. shared jackpots`} testid="bb-kpi-rtp" />
                        <Kpi label="Unique players" value={rep.unique_players.toLocaleString()} testid="bb-kpi-players" />
                        <Kpi label="Avg bet" value={usd(rep.avg_bet)} testid="bb-kpi-avgbet" />
                        <Kpi label="Bonuses" value={rep.bonuses.total.toLocaleString()} sub={`Stampede ${rep.bonuses.stampede} · Gold Rush ${rep.bonuses.gold} · Pick ${rep.bonuses.pick}`} testid="bb-kpi-bonuses" />
                    </div>
                    {Object.keys(rep.jackpots_by_tier).length > 0 && (
                        <div className="flex flex-wrap gap-2" data-testid="bb-jp-tiers">
                            {Object.entries(rep.jackpots_by_tier).map(([t, v]) => (
                                <span key={t} className="px-3 py-1.5 rounded-lg border border-white/10 text-xs"><b className="text-white">{t}</b> <span className="text-[#A0AAB2]">{v.count}× · {usd(v.amount)}</span></span>
                            ))}
                        </div>
                    )}
                    {rep.trend.length > 0 && (
                        <table className="w-full text-xs" data-testid="bb-report-trend">
                            <thead><tr className="text-[#A0AAB2] text-left">{['Business day', 'Handle', 'Wins', 'Jackpots', 'GGR'].map((h) => <th key={h} className="py-1 pr-4 font-normal">{h}</th>)}</tr></thead>
                            <tbody className="font-mono">
                                {rep.trend.slice().reverse().map((r) => (
                                    <tr key={r.date} className="border-t border-white/5">
                                        <td className="py-1 pr-4 text-white">{r.date}</td>
                                        <td className="py-1 pr-4 text-[#ffd24a]">{usd(r.handle)}</td>
                                        <td className="py-1 pr-4 text-[#3aa0ff]">{usd(r.wins)}</td>
                                        <td className="py-1 pr-4 text-[#b06bff]">{usd(r.jackpots)}</td>
                                        <td className={`py-1 pr-4 ${r.ggr >= 0 ? 'text-[#3ad07a]' : 'text-[#ff5a5a]'}`}>{usd(r.ggr)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                    {rep.rounds.length > 0 && <Rounds rows={rep.rounds} />}
                </>
            )}
        </div>
    );
};

export default BuffaloBucksReportsAdminTab;
